import React, { useCallback, useState, useEffect } from 'react';
import {
  getAllCompanies,
  getStateAndCity,
  getUniversalMeasurement
} from '../../services/api/implementation/impl';
import FormInput from '../../../shared/components/ui/FormInput';
import Button from '../../../shared/components/ui/Button';
import { useForm, useWatch } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { capitalizeWords } from '../../utils/stringUtils';
import LandSmartFillPanel from './LandSmartFillPanel';
import { LAND_DRAFT_FIELD_TO_FORM } from '../../constants/land';

const AddLand = ({ onClose, onSubmit, isSubmitting }) => {
  const userId = Cookies.get('userId');
  /* 
    Data fetching
  */
  const { data: companies } = useQuery({
    queryKey: ['companies'],
    queryFn: () => getAllCompanies()
  });

  /* 
    Local state
  */
  const [owners, setOwners] = useState([
    {
      ownerId: Date.now(),
      khasras: [
        {
          khasraId: Date.now(),
          khasra_number: '',
          mainArea: '',
          subArea: '',
          khasra_map: '',
          khasra_p1b2: '',
          land_use_map: ''
        }
      ]
    }
  ]);

  // Pincode Related states
  const [city, setCity] = useState('Select');
  const [state, setState] = useState('-');
  const [district, setDistrict] = useState('Select');
  const [tehsil, setTehsil] = useState('Select');
  const [cityDropdownOptions, setCityDropdownOptions] = useState([]);
  const [districtDropdownOptions, setDistrictDropdownOptions] = useState([]);
  const [tehsilDropdownOptions, setTehsilDropdownOptions] = useState([]);

  // Total Area States
  const [totalMainArea, setTotalMainArea] = useState(0);
  const [totalSubArea, setTotalSubArea] = useState(0);
  const [totalArea, setTotalArea] = useState(0);
  const [totalMainAreaType, setTotalMainAreaType] = useState('sq/hectare'); // Default unit
  const [totalSubAreaType, setTotalSubAreaType] = useState('sq/gunta'); // Fixed unit
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  const fileUrls = useSelector((state) => state.file.fileUrls);
  const MultipleFiles = useSelector((state) => state.file.multifiles);

  /* 
    Hook form
  */
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues: {
      measuring_unit: 'sq/hectare',
      total_mainArea: 0,
      total_subArea: 0,
      sub_area_measuring_unit: 'none'
    }
  });

  const watchedMeasuringUnit = watch('measuring_unit');
  const watchedSubAreaUnit = watch('sub_area_measuring_unit');
  const watchedCompanyId = useWatch({ control, name: 'company_id' });

  const { data: universalMeasurements = [] } = useQuery({
    queryKey: ['universalMeasurement', watchedCompanyId],
    queryFn: () => getUniversalMeasurement(watchedCompanyId),
    enabled: Boolean(watchedCompanyId)
  });

  /* 
    Event handlers
  */
  const handlePincodeChange = useCallback(
    async (pincode) => {
      const cleanedPincode = pincode.replace(/[^0-9]/g, '');
      setValue('pincode', cleanedPincode);
      if (cleanedPincode.length === 6) {
        try {
          const pincodeList = await getStateAndCity(cleanedPincode);
          const postOfficeList = pincodeList[0].PostOffice;
          if (postOfficeList.length > 0) {
            setState(postOfficeList[0].State);
            const uniqueCircles = [...new Set(postOfficeList.map((office) => office.Name))];
            const uniqueDistricts = [...new Set(postOfficeList.map((office) => office.District))];
            const uniqueTehsils = [...new Set(postOfficeList.map((office) => office.Block))];
            setCityDropdownOptions(
              uniqueCircles.map((circle) => ({ value: circle, label: circle }))
            );
            setDistrictDropdownOptions(
              uniqueDistricts.map((district) => ({ value: district, label: district }))
            );
            setTehsilDropdownOptions(
              uniqueTehsils.map((tehsil) => ({ value: tehsil, label: tehsil }))
            );

            setValue('state', postOfficeList[0].State);
            setValue('city', uniqueCircles[0]);
            setValue('district', uniqueDistricts[0]);
            setValue('tehsil', uniqueTehsils[0]);

            setState(postOfficeList[0].State);
            setCity(uniqueCircles[0]);
            setDistrict(uniqueDistricts[0]);
            setTehsil(uniqueTehsils[0]);
          } else {
            setValue('state', '-');
            setValue('city', 'Select');
            setValue('district', 'Select');
            setValue('tehsil', 'Select');

            setState('-');
            setCity('Select');
            setDistrict('Select');
            setTehsil('Select');
            setCityDropdownOptions([]);
          }
        } catch (error) {
          toast.error('The entered pincode is invalid. Please enter a valid pincode.');
          setValue('state', '-');
          setValue('city', 'Select');
          setValue('district', 'Select');
          setValue('tehsil', 'Select');

          setState('-');
          setCity('Select');
          setDistrict('Select');
          setTehsil('Select');
          setCityDropdownOptions([]);
        }
      }
    },
    [setValue]
  );

  /** Allow digits + one decimal point, max 3 decimal places. */
  const sanitizeAreaInput = (raw) => {
    let value = String(raw ?? '').replace(/[^0-9.]/g, '');
    const firstDot = value.indexOf('.');
    if (firstDot !== -1) {
      value =
        value.slice(0, firstDot + 1) + value.slice(firstDot + 1).replace(/\./g, '');
      const [whole, frac = ''] = value.split('.');
      value = `${whole}.${frac.slice(0, 3)}`;
    }
    return value.slice(0, 12);
  };

  const handleMainAreaChange = useCallback(
    (ownerId, khasraId, value) => {
      const cleanedValue = sanitizeAreaInput(value);
      setOwners((prevOwners) =>
        prevOwners.map((owner) =>
          owner.ownerId === ownerId
            ? {
              ...owner,
              khasras: owner.khasras.map((khasra) =>
                khasra.khasraId === khasraId
                  ? { ...khasra, mainArea: Number(cleanedValue) || 0 }
                  : khasra
              )
            }
            : owner
        )
      );
      setValue(`mainArea-${khasraId}`, cleanedValue);
    },
    [setValue]
  );

  const handleSubAreaChange = useCallback(
    (ownerId, khasraId, e) => {
      // Ensure we're getting the value correctly, whether it's an event or direct value
      const inputValue = e?.target?.value ?? e;
      const numericValue = sanitizeAreaInput(inputValue);

      setValue(`subArea-${khasraId}`, numericValue);

      setOwners((prevOwners) =>
        prevOwners.map((owner) =>
          owner.ownerId === ownerId
            ? {
              ...owner,
              khasras: owner.khasras.map((khasra) =>
                khasra.khasraId === khasraId
                  ? { ...khasra, subArea: Number(numericValue) || 0 }
                  : khasra
              )
            }
            : owner
        )
      );

      // Calculate total sub area
      let total = 0;
      owners.forEach((owner) => {
        owner.khasras.forEach((khasra) => {
          if (khasra.khasraId === khasraId) {
            total += Number(numericValue) || 0;
          } else {
            total += Number(khasra.subArea) || 0;
          }
        });
      });
      setTotalSubArea(total);
    },
    [setValue, owners]
  );

  // Update total main and sub areas whenever owners state changes

  // Track page view
  useEffect(() => {
    mixpanel.track('Add Land Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    const totalMain = owners.reduce(
      (sum, owner) =>
        sum + owner.khasras.reduce((kSum, khasra) => kSum + (parseFloat(khasra.mainArea) || 0), 0),
      0
    );
    const totalSub = owners.reduce(
      (sum, owner) =>
        sum + owner.khasras.reduce((kSum, khasra) => kSum + (parseFloat(khasra.subArea) || 0), 0),
      0
    );
    setTotalMainArea(totalMain);
    setTotalSubArea(totalSub);
    setValue('total_mainArea', totalMain);
    setValue('total_subArea', totalSub);
  }, [owners, setValue]);

  const handleAddKhasraBlock = useCallback((ownerId) => {
    setOwners((prevOwners) =>
      prevOwners.map((owner) =>
        owner.ownerId === ownerId
          ? {
            ...owner,
            khasras: [
              ...owner.khasras,
              {
                khasraId: Date.now(),
                khasra_number: '',
                mainArea: '',
                subArea: '',
                khasra_map: '',
                khasra_p1b2: '',
                land_use_map: ''
              }
            ]
          }
          : owner
      )
    );
  }, []);

  const handleRemoveKhasraBlock = useCallback((ownerId, khasraId) => {
    setOwners((prevOwners) =>
      prevOwners.map((owner) =>
        owner.ownerId === ownerId
          ? {
            ...owner,
            khasras: owner.khasras.filter((khasra) => khasra.khasraId !== khasraId)
          }
          : owner
      )
    );
  }, []);

  const handleAddOwnerBlock = useCallback(() => {
    setOwners((prevOwners) => [
      ...prevOwners,
      {
        ownerId: Date.now(),
        khasras: [
          {
            khasraId: Date.now(),
            khasra_number: '',
            mainArea: '',
            subArea: '',
            khasra_map: '',
            khasra_p1b2: '',
            land_use_map: ''
          }
        ]
      }
    ]);
  }, []);

  const handleRemoveOwnerBlock = useCallback((ownerId) => {
    setOwners((prevOwners) => prevOwners.filter((owner) => owner.ownerId !== ownerId));
  }, []);

  // Effect to handle sub area unit changes
  useEffect(() => {
    const isNone = watchedSubAreaUnit === 'none';
    if (isNone) {
      // Clear sub area values when 'none' is selected
      owners.forEach((owner) => {
        owner.khasras.forEach((khasra) => {
          setValue(`subArea-${khasra.khasraId}`, '');
          setOwners((prevOwners) =>
            prevOwners.map((o) =>
              o.ownerId === owner.ownerId
                ? {
                  ...o,
                  khasras: o.khasras.map((k) =>
                    k.khasraId === khasra.khasraId ? { ...k, subArea: 0 } : k
                  )
                }
                : o
            )
          );
        });
      });
      setTotalSubArea(0);
    }
  }, [watchedSubAreaUnit, setValue, owners]);

  /* 
    Helpers to resolve universal measurement info from a dropdown value
  */
  const STATIC_MAIN_UNITS = ['sq/hectare', 'sq/acres'];
  const STATIC_SUB_UNITS = ['none', 'guntha', 'cents'];

  // Returns the universal measurement record if value is a universal unit, else null
  const findUniversal = (value) =>
    universalMeasurements.find((m) => m.name === value) ?? null;

  const resolveMainAreaType = (value) => {
    if (STATIC_MAIN_UNITS.includes(value)) return value;
    const match = findUniversal(value);
    return match ? match.measurement_type : value;
  };

  const resolveSubAreaType = (value) => {
    if (value === 'none') return 'none';
    if (value === 'cents') return 'sq/cents';
    if (value === 'guntha') return 'sq/gunta';
    const match = findUniversal(value);
    return match ? match.measurement_type : value;
  };

  const resolveUniversalId = (value, staticList) => {
    if (staticList.includes(value)) return undefined;
    const match = findUniversal(value);
    return match ? match.id : undefined;
  };

  // Converts entered area value using universal per_unit factor.
  // e.g. "tago" has per_unit=10 (sq/ft each), user enters 100 → sends 1000
  const convertArea = (rawValue, value, staticList) => {
    const num = Number(rawValue) || 0;
    if (staticList.includes(value)) return num;
    const match = findUniversal(value);
    if (!match) return num;
    return num * (Number(match.per_unit) || 1);
  };

  /* 
    Form submission handler
  */
  const onSubmitHandler = (formData) => {
    const mainUnit = formData.measuring_unit;
    const subUnit = formData.sub_area_measuring_unit;

    const mainAreaType = resolveMainAreaType(mainUnit);
    const subAreaType = resolveSubAreaType(subUnit);
    const mainUniversalId = resolveUniversalId(mainUnit, STATIC_MAIN_UNITS);
    const subUniversalId = resolveUniversalId(subUnit, STATIC_SUB_UNITS);

    // Transform owners data to match API format
    const owner = owners.map((owner) => ({
      name: formData[`name-${owner.ownerId}`],
      email: formData[`email-${owner.ownerId}`] || '',
      phone_number: formData[`phone_number-${owner.ownerId}`],
      aadhar: formData[`aadhar-${owner.ownerId}`] || '',
      pan_number: formData[`pan-${owner.ownerId}`],
      diversion_order_link: fileUrls[`diversion_order-${owner.ownerId}`] || [],
      khasra: owner.khasras.map((khasra) => {
        const rawMain = formData[`mainArea-${khasra.khasraId}`];
        const rawSub = formData[`subArea-${khasra.khasraId}`];
        return {
          number: formData[`khasra_number-${khasra.khasraId}`],
          mainArea: convertArea(rawMain, mainUnit, STATIC_MAIN_UNITS),
          subArea:
            subUnit === 'none'
              ? 0
              : convertArea(rawSub || 0, subUnit, STATIC_SUB_UNITS),
          mainAreaType,
          subAreaType,
          ...(mainUniversalId !== undefined && {
            main_area_universal_measurement_id: mainUniversalId
          }),
          ...(subUniversalId !== undefined && {
            sub_area_universal_measurement_id: subUniversalId
          }),
          khasra_map_link: fileUrls[`khasra_map-${khasra.khasraId}`] || [],
          khasra_p1b2_link: fileUrls[`khasra_p1b2-${khasra.khasraId}`] || [],
          land_use_map: fileUrls[`land_use_map-${khasra.khasraId}`] || []
        };
      })
    }));

    const payload = {
      owner,
      name: formData.name,
      company_id: parseInt(formData.company_id),
      registry_doc_link: MultipleFiles?.registry_document || [],
      advocate_search_link: MultipleFiles?.advocate_document || [],
      measuringUnit: mainAreaType,
      total_mainArea: convertArea(formData.total_mainArea, mainUnit, STATIC_MAIN_UNITS),
      total_subArea: convertArea(totalSubArea, subUnit, STATIC_SUB_UNITS),
      total_mainAreaType: mainAreaType,
      total_subAreaType: subAreaType,
      ...(mainUniversalId !== undefined && {
        total_main_area_universal_measurement_id: mainUniversalId
      }),
      ...(subUniversalId !== undefined && {
        total_sub_area_universal_measurement_id: subUniversalId
      }),
      pincode: parseInt(formData.pincode),
      state: formData.state,
      district: formData.district,
      tehsil: formData.tehsil,
      area: formData.area || '',
      google_map_link: formData.google_map_link || '',
      additional_documents_link: MultipleFiles?.land_map || []
    };

    console.log('payload === >>>', { payload });
    onSubmit(payload);
  };

  const handleAadharChange = useCallback(
    async (aadhar, key) => {
      const cleanedAadhar = aadhar.replace(/[^0-9]/g, '').slice(0, 12); // Aadhar card is 12 digits long
      setValue(key, cleanedAadhar);
    },
    [setValue]
  );

  const handlePhoneNumber = useCallback(
    async (val, key) => {
      const cleanedValue = val.replace(/[^0-9]/g, '').slice(0, 10);
      setValue(key, cleanedValue);
    },
    [setValue]
  );

  /**
   * Values placed by Smart Fill, keyed by form field name. Each entry keeps the model's
   * confidence, source document and evidence quote so the value can be checked against
   * the document. Editing a field drops it from here — a confirmed value belongs to the
   * user and must not keep advertising a model's confidence score.
   */
  const [aiFields, setAiFields] = useState({});

  /**
   * Drop the ✨ mark from any field the user has since changed.
   *
   * Done by comparing current form values against what was placed, rather than by
   * threading a clear-me call through every onType handler: the location fields are
   * rewritten wholesale by the pincode lookup, so a per-handler approach would miss them
   * and leave a stale confidence score on a value the model never produced.
   */
  const watchedFormValues = watch();
  useEffect(() => {
    setAiFields((previous) => {
      const stale = Object.entries(previous).filter(
        ([formField, meta]) =>
          String(watchedFormValues?.[formField] ?? '') !== String(meta.value ?? '')
      );
      if (stale.length === 0) return previous; // no state change, so no re-render loop
      const next = { ...previous };
      stale.forEach(([formField]) => delete next[formField]);
      return next;
    });
  }, [watchedFormValues]);

  /**
   * Fold a reviewed draft into the form.
   *
   * Owners and parcels REPLACE the owner blocks rather than merging into them: the draft
   * has already been reviewed row by row in the panel, and merging two parcel lists in
   * the form would reintroduce exactly the index-matching bug the backend avoids by
   * matching khasra on number. An empty owner list leaves the existing blocks alone.
   */
  const applyDraft = useCallback(
    (draft) => {
      const applied = {};

      Object.entries(draft?.fields || {}).forEach(([canonicalName, field]) => {
        if (!field?.value) return;

        const formField = LAND_DRAFT_FIELD_TO_FORM[canonicalName];
        if (formField) {
          setValue(formField, field.value, { shouldValidate: true });
          applied[formField] = {
            value: field.value,
            confidence: field.confidence,
            evidence: field.evidence,
            source: field.source,
            conflict: field.conflict
          };
        }

        // The location dropdowns are driven by component state as well as the form, so
        // both have to be set or the visible selection and the submitted value diverge.
        if (canonicalName === 'state') setState(field.value);
        if (canonicalName === 'district') setDistrict(field.value);
        if (canonicalName === 'tehsil') setTehsil(field.value);
        if (canonicalName === 'total_main_area_unit') {
          setValue('measuring_unit', field.value);
          setTotalMainAreaType(field.value);
        }
      });

      const draftOwners = draft?.owners || [];
      const draftKhasra = draft?.khasra || [];

      if (draftOwners.length > 0 || draftKhasra.length > 0) {
        const owners = draftOwners.length > 0 ? draftOwners : [{ name: '' }];
        const base = Date.now();

        const nextOwners = owners.map((owner, ownerIndex) => {
          const ownerId = base + ownerIndex * 1000;

          setValue(`name-${ownerId}`, owner.name || '');
          setValue(`phone_number-${ownerId}`, owner.phone_number || '');
          setValue(`pan-${ownerId}`, owner.pan_number || '');
          // Aadhaar is deliberately never filled by Smart Fill — it is not extracted at
          // all, so there is nothing to place here. It stays a manual field.

          // Parcels the document tied to this owner; anything unattributed goes to the
          // first block so no reviewed row is silently dropped.
          const mine = draftKhasra.filter((parcel) =>
            owner.pan_number
              ? parcel.owner_pan === owner.pan_number
              : !parcel.owner_pan || ownerIndex === 0
          );
          const parcels = mine.length > 0 ? mine : [{}];

          return {
            ownerId,
            khasras: parcels.map((parcel, parcelIndex) => {
              const khasraId = ownerId + parcelIndex + 1;
              setValue(`khasra_number-${khasraId}`, parcel.number || '');
              setValue(`mainArea-${khasraId}`, parcel.main_area || '');
              setValue(`subArea-${khasraId}`, parcel.sub_area || '');
              return {
                khasraId,
                khasra_number: parcel.number || '',
                mainArea: parcel.main_area || '',
                subArea: parcel.sub_area || '',
                khasra_map: '',
                khasra_p1b2: '',
                land_use_map: ''
              };
            })
          };
        });

        setOwners(nextOwners);
      }

      setAiFields((previous) => ({ ...previous, ...applied }));
    },
    [setValue]
  );

  const aiCount = Object.keys(aiFields).length;

  const handleLandName = useCallback(
    async (val) => {
      const newval = val.replace(/[^a-zA-Z\s]/g, ''); // Only allow alphabets and spaces
      setValue('name', capitalizeWords(newval));
    },
    [setValue]
  );

  const handleOwnerName = useCallback(
    async (val, key) => {
      const newval = val.replace(/[^a-zA-Z\s]/g, ''); // Only allow alphabets and spaces
      setValue(key, capitalizeWords(newval));
    },
    [setValue]
  );

  const handlePanChange = useCallback(
    async (pan, key) => {
      const cleanedPan = pan.toUpperCase().replace(/[^A-Z0-9]/g, '');
      setValue(key, cleanedPan);
    },
    [setValue]
  );

  const handleLocalityChange = useCallback(
    async (val) => {
      const cleanedValue = val.replace(/[^a-zA-Z\s]/g, '').slice(0, 50);
      setValue('area', cleanedValue);
    },
    [setValue]
  );

  // Watch measuring_unit and store it in a state
  useEffect(() => {
    setTotalMainAreaType(watchedMeasuringUnit);
  }, [watchedMeasuringUnit]);

  useEffect(() => {
    setTotalSubAreaType(watchedSubAreaUnit ?? 'none');
  }, [watchedSubAreaUnit]);

  // Watch state
  const watchStateChange = useWatch({
    control,
    name: 'state',
    defaultValue: ''
  });

  const [watchState, setWatchState] = useState(null);

  useEffect(() => {
    setWatchState(watchStateChange?.toLowerCase());
  }, [watchStateChange]);

  //writw a function to return "sq/gunta" to "gunta" and "sq/hectares" to "hectares"
  const formatAreaType = (areaType) => {
    return areaType?.replace('sq/', '');
  };

  // Helper function to check if state uses Survey terminology
  const isSurveyState = (state) => {
    const surveyStates = [
      'andhra pradesh',
      'telangana',
      'karnataka',
      'kerala',
      'tamil nadu',
      'gujarat'
    ];
    return surveyStates.includes(state?.toLowerCase());
  };

  // Helper function to check if state uses Dag terminology
  const isDagState = (state) => {
    return state?.toLowerCase() === 'west bengal';
  };

  const getLandLabel = (state) => {
    if (isDagState(state)) return 'Dag';
    if (isSurveyState(state)) return 'Survey';
    return 'Khasra';
  };


  /* 
    JSX
  */
  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmitHandler)}>
      {/*
        Strictly additive. With LAND_ONBOARDING_AI_ENABLED off the panel reports that and
        every field below is filled in by hand exactly as before — the manual path is the
        default, not the fallback.
      */}
      <LandSmartFillPanel onApplyDraft={applyDraft} disabled={isSubmitting} />

      {aiCount > 0 && (
        <p className="text-xs text-stone-500">
          ✨ {aiCount} field{aiCount === 1 ? '' : 's'} filled from your document. Check each
          one against the document — nothing here has been verified against a government
          record. Editing a field makes it yours.
        </p>
      )}

      {/* Land Details Section */}
      <div className="flex flex-col justify-between items-start gap-4">
        <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">Land Details</span>

        <div className="flex flex-col w-full flex-1 gap-4">
          <div className="flex space-x-8 justify-between items-start w-full">
            <div className="flex flex-col w-full flex-1 h-full items-stretch gap-6">
              <div className="w-full flex flex-col gap-4 flex-1">
                <div className="flex justify-between space-x-4">
                  <FormInput
                    width={32}
                    register={register}
                    isRequired={true}
                    register_key={'name'}
                    label={'Land Name'}
                    type={'text'}
                    onType={handleLandName}
                  />

                  <FormInput
                    width={32}
                    register={register}
                    register_key={'company_id'}
                    isRequired={true}
                    label={'Company name'}
                    type={'dropdown'}
                    options={[
                      { value: '', label: 'Select Company' },
                      ...(companies
                        ? companies.map((item) => ({
                          value: item?.id,
                          label: item?.company_name?.trim()
                        }))
                        : [])
                    ]}
                  />

                  <FormInput
                    width={32}
                    register={register}
                    register_key={'registry_document'}
                    label={'Registry Document'}
                    type={'file-secondary'}
                  />
                </div>
              </div>

              <div className="w-full flex justify-start space-x-4 gap-3 items-top pb-6">
                <FormInput
                  width={32}
                  register={register}
                  register_key={'advocate_document'}
                  label={'Advocate Search'}
                  type={'file-secondary'}
                />
                {/* <FormInput
                  width={32}
                  label={'Khasra Area Measurement Type'}
                  register={register}
                  register_key={'measuring_unit'}
                  isRequired={true}
                  type={'dropdown'}
                  options={[
                    {
                      value: 'sq/hectare',
                      label: 'hectares',
                      selected: true
                    },
                    {
                      value: 'sq/acres',
                      label: 'acres'
                    }
                  ]}
                  errors={errors.measuring_unit}
                /> */}

                {/* Sub Area Measuring Unit (Fixed to Gunta) */}
                {/* <FormInput
                  width={32}
                  register={register}
                  register_key={'sub_area_measuring_unit'}
                  // label={`Sub area measuring unit`}
                  type={'text'}
                  valuePre={'guntha'}
                  CustomStyle={{ pointerEvents: 'none', backgroundColor: '#E7E5E4' }}
                /> */}

                <div className="flex flex-col w-[32%]">
                  <label className="text-md text-gray-600 mb-2">
                    {getLandLabel(watchState)} Area Measurement Type
                  </label>

                  <div className="flex items-center justify-between gap-4">
                    {/* Main area measuring unit */}
                    <select
                      className="border border-gray-300 rounded-md px-3 py-2 w-1/2"
                      {...register('measuring_unit', { required: true })}>
                      <option value="sq/hectare">Hectares</option>
                      <option value="sq/acres">Acres</option>
                      {universalMeasurements.map((item) => (
                        <option key={`main-${item.id}`} value={item.name}>
                          {item.name}
                        </option>
                      ))}
                    </select>

                    {/* Sub area measuring unit */}
                    <select
                      className="border border-gray-300 rounded-md px-3 py-2 w-1/2"
                      {...register('sub_area_measuring_unit', { required: true })}
                      defaultValue="none">
                      <option value="none">None</option>
                      <option value="guntha">Guntha</option>
                      <option value="cents">Cents / decimal</option>
                      {universalMeasurements.map((item) => (
                        <option key={`sub-${item.id}`} value={item.name}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Error messages (optional) */}
                  {errors.measuring_unit && (
                    <p className="text-red-500 text-sm mt-1">{errors.measuring_unit.message}</p>
                  )}
                  {errors.sub_area_measuring_unit && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.sub_area_measuring_unit.message}
                    </p>
                  )}
                </div>
                <div className="flex w-[32%] gap-4 items-center">
                  <FormInput
                    width={55}
                    register={register}
                    isRequired={true}
                    register_key={'total_main_area'}
                    label={`Total ${getLandLabel(watchState)} area (${formatAreaType(watchedMeasuringUnit)})`}
                    type={'text'}
                    valuePre={totalMainArea || 0}
                    CustomStyle={{ pointerEvents: 'none', backgroundColor: '#E7E5E4' }}
                  />
                  <FormInput
                    width={40}
                    register={register}
                    isRequired={true}
                    register_key={'total_sub_area'}
                    label={`Total${totalSubAreaType && totalSubAreaType !== 'none'
                      ? totalSubAreaType === 'cents'
                        ? ' (cents / decimal)'
                        : ` (${totalSubAreaType})`
                      : ''
                      }`}
                    type={'text'}
                    valuePre={totalSubArea || 0}
                    CustomStyle={{ pointerEvents: 'none', backgroundColor: '#E7E5E4' }}
                  />
                </div>
              </div>

              <div className="w-full flex justify-between space-x-4 items-center">
                <FormInput
                  width={32}
                  register={register}
                  isRequired={true}
                  register_key={'pincode'}
                  label={'Pincode'}
                  type={'text'}
                  maxLength={6}
                  onType={handlePincodeChange}
                />

                <FormInput
                  width={32}
                  register={register}
                  isRequired={true}
                  register_key={'land_use_map'}
                  label={'Land use map'}
                  type={'hidden'}
                />

                <FormInput
                  width={32}
                  register={register}
                  register_key={'state'}
                  label={`State`}
                  errors={errors.state}
                  valuePre={state}
                  CustomStyle={{ pointerEvents: 'none' }}
                />

                <FormInput
                  width={32}
                  register={register}
                  register_key={'district'}
                  label={'District'}
                  type={'dropdown'}
                  defaultValuePre={district}
                  options={districtDropdownOptions}
                />
              </div>

              <div className="w-full flex justify-start space-x-4 items-center pb-6">
                <FormInput
                  width={32}
                  register={register}
                  register_key={'tehsil'}
                  label={`${isSurveyState(watchState) ? 'Taluk' : 'Tehsil'}`}
                  type={'dropdown'}
                  defaultValuePre={tehsil}
                  options={tehsilDropdownOptions}
                />

                <FormInput
                  width={32}
                  register={register}
                  register_key={'area'}
                  label={'Locality'}
                  type={'text'}
                  onType={handleLocalityChange}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Owner Details Section */}
      <div className="flex flex-col justify-between items-start gap-4">
        <span className="text-lg w-full pb-2">Owner Details</span>

        {owners.map((owner, ownerIndex) => (
          <div
            className={`flex flex-col w-full flex-1 gap-4 ${ownerIndex > 0 ? 'pt-6' : 'pb-6'} border-[1px] rounded-md p-3`}
            key={owner.ownerId}>
            <div className="flex space-x-8 justify-between items-start w-full">
              <div className="flex flex-col w-full flex-1 h-full items-stretch gap-6">
                <div className="w-full flex flex-col gap-4 flex-1">
                  <div className="flex justify-between space-x-4">
                    {/* Mandatory Fields */}
                    <FormInput
                      width={32}
                      register={register}
                      isRequired={true}
                      register_key={`name-${owner.ownerId}`}
                      label={'Owner Name'}
                      type={'text'}
                      onType={(val) => handleOwnerName(val, `name-${owner.ownerId}`)}
                    />

                    <FormInput
                      width={32}
                      register={register}
                      isRequired={true}
                      register_key={`phone_number-${owner.ownerId}`}
                      label={'Mobile Number'}
                      type={'text'}
                      onType={(val) => handlePhoneNumber(val, `phone_number-${owner.ownerId}`)}
                    />

                    <FormInput
                      width={32}
                      register={register}
                      isRequired={true}
                      register_key={`pan-${owner.ownerId}`}
                      label={'PAN number'}
                      type={'text'}
                      onType={(val) => handlePanChange(val, `pan-${owner.ownerId}`)}
                    />
                  </div>
                </div>

                <div className="w-full flex justify-start space-x-6 items-center">
                  {/* Optional Fields */}
                  <FormInput
                    width={32}
                    register={register}
                    register_key={`email-${owner.ownerId}`}
                    label={'Email'}
                  />

                  <FormInput
                    width={32}
                    register={register}
                    register_key={`aadhar-${owner.ownerId}`}
                    label={'Aadhar number'}
                    type={'text'}
                    onType={(val) => handleAadharChange(val, `aadhar-${owner.ownerId}`)}
                  />

                  <FormInput
                    width={32}
                    register={register}
                    register_key={`diversion_order-${owner.ownerId}`}
                    label={`${isSurveyState(watchState) ? 'Conversion Order' : 'Diversion Order'}`}
                    type={'file-btn'}
                  />
                </div>
              </div>
            </div>

            {/* Khasra Details */}
            {owner.khasras.map((khasra, khasraIndex) => (
              <div key={khasra.khasraId} className="flex flex-col gap-6 bg-gray-50 rounded-md p-3">
                <div className="w-full flex justify-between space-x-4 items-center pt-6">
                  <FormInput
                    width={20}
                    register={register}
                    register_key={`khasra_map-${khasra.khasraId}`}
                    label={`${getLandLabel(watchState)} Map`}
                    type={'file-btn'}
                  />

                  <FormInput
                    width={20}
                    register={register}
                    register_key={`khasra_p1b2-${khasra.khasraId}`}
                    label={`${isSurveyState(watchState) ? 'Podi / Hissas ' : 'Khasra P1B2'}`}
                    type={'file-btn'}
                    accept={'.pdf'}
                  />

                  <FormInput
                    width={20}
                    register={register}
                    register_key={`land_use_map-${khasra.khasraId}`}
                    label={'Land Use Certificate'}
                    type={'file-btn'}
                    accept={'.pdf'}
                  />

                  <FormInput
                    width={30}
                    register={register}
                    isRequired={true}
                    register_key={`khasra_number-${khasra.khasraId}`}
                    label={`${getLandLabel(watchState)} Number`}
                    type={'text'}
                  />
                </div>

                {/* Khasra Area Inputs */}
                <div className="w-full flex justify-start space-x-4 items-center">
                  {/* Main Area Input */}
                  <FormInput
                    width={30}
                    register={register}
                    isRequired={true}
                    register_key={`mainArea-${khasra.khasraId}`}
                    label={`${getLandLabel(watchState)} Area (${formatAreaType(totalMainAreaType)})`}
                    type={'textOnType'}
                    valuePre={watch(`mainArea-${khasra.khasraId}`) ?? ''}
                    onType={(e) =>
                      handleMainAreaChange(owner.ownerId, khasra.khasraId, e.target.value)
                    }
                  />

                  {/* Sub Area Input (Guntha/Cents) */}
                  <FormInput
                    width={48}
                    label={`Sub Area${watchedSubAreaUnit !== 'none'
                      ? ` (${watchedSubAreaUnit === 'cents' ? 'cents / decimal' : watchedSubAreaUnit})`
                      : ''
                      }`}
                    register={register}
                    register_key={`subArea-${khasra.khasraId}`}
                    type={'textOnType'}
                    valuePre={watch(`subArea-${khasra.khasraId}`) ?? ''}
                    readOnly={watchedSubAreaUnit === 'none'}
                    CustomStyle={
                      watchedSubAreaUnit === 'none' ? { backgroundColor: '#E7E5E4' } : {}
                    }
                    onType={(e) => handleSubAreaChange(owner.ownerId, khasra.khasraId, e)}
                  />
                </div>

                <div className="w-full flex justify-start space-x-4 items-center">
                  {owner.khasras.length > 1 && (
                    <div className="w-full flex justify-end mt-4 self-end">
                      <span
                        className="text-stone-700 underline underline-offset-2 transition-all duration-300 hover:scale-[102%] cursor-pointer mt-4"
                        onClick={() => handleRemoveKhasraBlock(owner.ownerId, khasra.khasraId)}>
                        - Remove khasra details
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}

            <div className="w-full flex justify-end self-end">
              <span
                className="text-stone-700 underline underline-offset-2 transition-all duration-300 hover:scale-[102%] cursor-pointer mt-4"
                onClick={() => handleAddKhasraBlock(owner.ownerId)}>
                + Add more {getLandLabel(watchState)} details
              </span>
            </div>
            {owners.length > 1 && (
              <div className="w-full flex justify-end mt-4 self-end">
                <span
                  className="text-stone-700 border-customBlue border py-2 px-4 rounded-lg transition-all duration-300 hover:scale-[102%] cursor-pointer mt-4"
                  onClick={() => handleRemoveOwnerBlock(owner.ownerId)}>
                  - Remove owner details
                </span>
              </div>
            )}
          </div>
        ))}
        <div className="w-full flex justify-end mt-4 self-end">
          <span
            className="text-stone-700 border-customBlue border py-2 px-4 rounded-lg transition-all duration-300 hover:scale-[102%] cursor-pointer mt-4"
            onClick={handleAddOwnerBlock}>
            + Add more owner details
          </span>
        </div>
      </div>

      {/* Additional Details Section */}
      <div className="flex flex-col justify-between items-start gap-4 pt-6">
        <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">
          Additional Details
        </span>

        <div className="flex flex-col w-full flex-1 gap-4">
          <div className="flex space-x-8 justify-between items-start w-full">
            <div className="flex flex-col w-full flex-1 h-full items-stretch gap-6">
              <div className="w-full flex flex-col gap-4 flex-1">
                <div className="flex justify-start space-x-4">
                  <FormInput
                    width={32}
                    register={register}
                    register_key={'google_map_link'}
                    errors={errors.google_map_link}
                    label={'Google Map Link'}
                    type={'textOnType'}
                  />

                  <FormInput
                    width={32}
                    register={register}
                    register_key={'land_map'}
                    label={'Additional Documents'}
                    type={'file-secondary'}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="w-full flex justify-end items-center space-x-4 mt-8">
        <Button type="reset" onClick={onClose} displayType="secondary">
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          Submit
        </Button>
      </div>
    </form>
  );
};

export default AddLand;
